type ParsedApiKey = {
  environment: string;
  workspaceId: string;
  tenantId: string;
  baseUrl: string;
};

const DEFAULT_BASE_URLS: Record<string, string> = {
  live: 'https://2yineckh1e.execute-api.eu-central-1.amazonaws.com/'
};

/**
 * Parses an API key to extract workspaceId, tenantId, and baseUrl
 * Expected format: cs_{environment}_tenantId_workspaceId_secret
 * Matches backend parsing logic
 * 
 * @param apiKey - The API key to parse
 * @returns ParsedApiKey object with extracted data
 * @throws Error if the API key format is invalid
 */
export const parseApiKey = (apiKey: string): ParsedApiKey => {
  if (!apiKey || typeof apiKey !== 'string') {
    throw new Error('API key must be a non-empty string');
  }

  // Check if it starts with cs_ prefix
  if (!apiKey.startsWith('cs_')) {
    throw new Error('Invalid API key format. Must start with "cs_"');
  }

  // Find the environment (cs_live_, cs_test_, cs_dev_, etc.)
  const parts = apiKey.split('_');
  if (parts.length < 2) {
    throw new Error('Invalid API key format. Must include environment (e.g., cs_live_)');
  }

  const environment = parts[1];
  const prefix = `cs_${environment}_`;
  
  if (!apiKey.startsWith(prefix)) {
    throw new Error(`Invalid API key format. Must start with "${prefix}"`);
  }

  // Remove prefix (e.g., 'cs_live_')
  const withoutPrefix = apiKey.substring(prefix.length);

  // Split by underscore - format: tenantId_workspaceId_secret
  const keyParts = withoutPrefix.split('_');

  if (keyParts.length < 3) {
    throw new Error('Invalid API key format. Expected format: cs_{environment}_tenantId_workspaceId_secret');
  }

  const tenantId = keyParts[0];
  const workspaceId = keyParts[1];
  // The rest (keyParts[2] onwards) is the secret, but we don't need it for parsing

  if (!tenantId) {
    throw new Error('Tenant ID not found in API key');
  }

  if (!workspaceId) {
    throw new Error('Workspace ID not found in API key');
  }

  // Determine baseUrl from environment
  const baseUrl = DEFAULT_BASE_URLS[environment] || DEFAULT_BASE_URLS.live;

  return {
    environment,
    workspaceId,
    tenantId,
    baseUrl,
  };
};

